import React from 'react';
import BiogasDataGrid from '../../grids/biogas/BiogasDataGrid';
import { useCreateBiogasData, useDeleteBiogasData, useUpdateEpdGeneralInformation } from 'services/api/mutations';
import { useBiogas, useEPDDefinitionInformation } from 'services/api/queries';
import { YES_NO, YesNoDataSource } from '../../constants';
import CompilerSelectControlledComponent from 'components/v2/epd-compiler-inputs/CompilerSelectControlledComponent';
import { BiogasDataModel } from 'types/types';
import {
  ExpandablePanelHeaderName,
  ExpandablePanelModule,
  HeaderCaptionSemiBold,
  IconsSection,
  StyledIconButton,
} from 'styles/v2/Styles.styled';
import TooltipHelpIcon from 'components/v2/icons/TooltipHelpIcon';
import ToggleButton from '../toggleable/ToggleButton';
import TooltipErrorIcon from 'components/v2/icons/TooltipErrorIcon';
import { AddCustomBlockButton } from 'components/v2/buttons/AddCustomBlockButton';
import useIsReadOnlyMode from '../../hooks/useIsReadOnlyMode';
import FontIcon from 'components/icons/FontIcon';
import { confirmDelete } from 'components/v2/confirm-dialog/confirmDelete';

interface BiogasDataProps {
  epdId: string;
  epdVersionId: string;
  errors: any;
}

const BiogasDataPanelsList: React.FC<BiogasDataProps> = ({ epdId, epdVersionId, errors }: BiogasDataProps) => {
  const biogasData = useBiogas(epdVersionId).data;
  const createBiogasMutation = useCreateBiogasData(epdVersionId);
  const deleteBiogasMutation = useDeleteBiogasData(epdVersionId);

  const epdDefinitionInformation = useEPDDefinitionInformation(epdVersionId).data;
  const updateMutation = useUpdateEpdGeneralInformation(epdVersionId);
  const isReadOnly = useIsReadOnlyMode();

  const getBiogasSectionErrors = (index: number) => {
    if (errors.biogasData) {
      return errors.biogasData[`biogasData[${index}]`];
    }
    return null;
  };

  const confirmDeleteBiogasSection = (biogasData: BiogasDataModel) => {
    const biogasUsedInProcessName = biogasData.usedInProcessName
      ? `Biogas used in ${biogasData.usedInProcessName}`
      : 'Biogas used in the unnamed process';
    confirmDelete({
      caption: `Are you sure you want to delete ${biogasUsedInProcessName}?`,
      onConfirm: () => deleteBiogasMutation.mutate([biogasData.id!]),
    });
  };

  const headerModuleTemplate = (
    options: any,
    header: string,
    tooltip: string | null,
    tooltipPosition?: any | undefined,
    errors?: any,
    biogasData?: BiogasDataModel | null | undefined
  ) => {
    return (
      <div className={`${options.className} ${options.collapsed ? 'header-collapsed' : ''}`}>
        <ExpandablePanelHeaderName>
          <HeaderCaptionSemiBold>{header}</HeaderCaptionSemiBold>
          {tooltip && <TooltipHelpIcon content={tooltip} position={tooltipPosition} />}
          {errors?.some((error: any) => !!error) && <TooltipErrorIcon content="Required" />}
          <ToggleButton options={options} />
        </ExpandablePanelHeaderName>
        {biogasData && (biogasData.ord as number) > 1 && !isReadOnly && (
          <IconsSection>
            <StyledIconButton
              icon={<FontIcon icon="delete" />}
              text
              onClick={() => confirmDeleteBiogasSection(biogasData)}
            />
          </IconsSection>
        )}
      </div>
    );
  };

  const onChangeBiogasApplicable = (propertyName: string, val: string) => {
    if (!epdId) return;

    if (val === YES_NO.YES && biogasData?.length === 0) {
      // Create new biogas data if it doesn't exist.
      const newBiogasData: BiogasDataModel = {
        epdVersionId: epdVersionId,
        usedInProcessName: 'Unnamed process',
      };
      createBiogasMutation.mutate(newBiogasData);
    } else if (val === YES_NO.NO && biogasData && biogasData.length > 0) {
      // Delete all biogas data to avoid problems during PDF generation.
      const ids = biogasData.map((x) => x.id).filter((id): id is string => typeof id === 'string');
      if (ids.length > 0) {
        deleteBiogasMutation.mutate(ids);
      }
    }

    updateMutation.mutate({
      epdId,
      versionId: epdVersionId,
      propertyName,
      newValue: val,
    });
  };

  return (
    <>
      {epdDefinitionInformation?.biogasApplicable === YES_NO.NO && (
        <ExpandablePanelModule
          headerTemplate={(options) =>
            headerModuleTemplate(
              options,
              'Biogas Data',
              'Add information on how biogas has been modelled for biogas used in A3 processes (A5 processes for services).',
              undefined,
              null,
              null
            )
          }
          toggleable
        >
          <CompilerSelectControlledComponent
            label="Is biogas modelling applicable?"
            name="biogasApplicable"
            options={YesNoDataSource}
            value={YesNoDataSource.find((item) => item.value === epdDefinitionInformation?.biogasApplicable) || null}
            onChanged={onChangeBiogasApplicable}
            required
            placeholder="Select"
            isClearable={false}
            error={errors.biogasApplicable}
          />
        </ExpandablePanelModule>
      )}
      {Array.isArray(biogasData) && biogasData.length > 0
        ? biogasData.map((biogasItem: BiogasDataModel, index: number) => {
            const isDefault = biogasItem.ord === 1;
            return (
              <ExpandablePanelModule
                key={biogasItem.ord}
                headerTemplate={(options) =>
                  headerModuleTemplate(
                    options,
                    isDefault
                      ? 'Biogas Data: Biogas used in the manufacturing process in A3 (A5 for services)'
                      : `Biogas Data: Biogas used in ${
                          biogasItem.usedInProcessName ? biogasItem.usedInProcessName : 'unnamed process'
                        }`,
                    isDefault
                      ? 'Add information on how biogas has been modelled for biogas used in A3 processes (A5 processes for services).'
                      : 'Add information on how biogas has been modelled for other processes under the control of the EPD Owner. Specify the process under "Biogas used in".',
                    undefined,
                    [getBiogasSectionErrors(index)],
                    biogasItem
                  )
                }
                toggleable
              >
                {isDefault && (
                  <CompilerSelectControlledComponent
                    label="Is biogas modelling applicable?"
                    name="biogasApplicable"
                    options={YesNoDataSource}
                    value={YesNoDataSource.find((item) => item.value === epdDefinitionInformation?.biogasApplicable) || null}
                    onChanged={onChangeBiogasApplicable}
                    required
                    placeholder="Select"
                    isClearable={false}
                    error={errors.biogasApplicable}
                  />
                )}
                <>
                  <BiogasDataGrid
                    key={biogasItem.ord || index}
                    epdVersionId={epdVersionId}
                    errors={getBiogasSectionErrors(index)}
                    biogasData={biogasItem}
                  />
                </>
              </ExpandablePanelModule>
            );
          })
        : null}
      {Array.isArray(biogasData) && biogasData.length > 0 && !isReadOnly && (
        <AddCustomBlockButton
          onClick={() =>
            createBiogasMutation.mutate({
              epdVersionId: epdVersionId,
            })
          }
          caption="Add additional biogas table"
        />
      )}
    </>
  );
};

export default BiogasDataPanelsList;
